<template>
  <div class="modal-container">
    <div class="overlay" @click="$emit('close')"></div>
    <transition name="pop-in" appear>
      <div ref="modal" class="modal" :tabindex="hideClose ? -1 : null">
        <div v-if="title || !hideClose" class="modal-header">
          <div class="modal-title">
            {{ title }}
          </div>
          <BaseIcon
            v-if="!hideClose"
            ref="closeButton"
            tabindex="0"
            icon-file="icon-close-black"
            class="close-icon"
            @click="$emit('close')"
            @keydown.enter="$emit('close')"
          />
        </div>
        <div class="modal-body">
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import BaseIcon from './BaseIcon.vue';

export default {
  name: 'BaseModal',
  components: {
    BaseIcon
  },

  props: {
    title: {
      type: String,
      default: ''
    },
    // Renders a header-less dialog: no title bar, no close icon. Closing
    // is the caller's responsibility (own buttons; the overlay still
    // emits close).
    hideClose: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      lastFocusedElement: null
    };
  },

  watch: {
    $route(newVal, oldVal) {
      // Prevent closing on query change
      if (newVal.path !== oldVal.path) {
        this.$emit('close');
      }
    }
  },

  created() {
    this.lastFocusedElement = document.activeElement;
    this.$emit('open');
  },

  mounted() {
    if (this.hideClose) {
      // There's no close icon to move focus to — focus the dialog
      // itself so keyboard and screen-reader context enters the modal
      // instead of staying on the trigger element behind the overlay
      // (where Enter would re-fire it and Tab walks the background).
      this.$nextTick(() => {
        this.$refs.modal?.focus();
      });
      return;
    }
    const currentlyFocusedElement = document.activeElement;
    if (currentlyFocusedElement && currentlyFocusedElement !== document.body) {
      this.$nextTick(() => {
        const closeButton = this.$refs[`closeButton`];
        if (closeButton) {
          closeButton.$el.focus();
        }
      });
    }
  },

  destroyed() {
    this.lastFocusedElement?.focus();
  }
};
</script>

<style lang="scss" scoped>
.modal-container {
  position: fixed;
  height: 100vh;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
}

.modal {
  position: fixed;
  width: 96%;
  max-width: 400px;
  border-radius: 20px;
  background-color: #fff;
  overflow: hidden;
  z-index: 5000;

  // Programmatic focus target (hideClose) — no visible ring on the
  // dialog frame itself.
  &:focus {
    outline: none;
  }
}

.modal-header {
  position: relative;
  padding: 21px;
  border-bottom: 1px solid #e8e8e8;
}

.modal-title {
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: center;
}

.close-icon {
  position: absolute;
  right: 20px;
  top: calc(50% - 11px);
}

.icon-close {
  width: 22px;
  height: 22px;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  cursor: pointer;
}

.modal-body {
  max-height: 90vh;
  padding: 20px 40px;
  overflow-y: auto;

  @media ($desktop) {
    padding: 20px 56px;
  }
}

.overlay {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.4);
  height: 100vh;
  width: 100vw;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2000;
}
</style>
